<script setup lang="ts">
import { ref, watch } from "vue";
import { UiDialog, UiCurrencyInput, UiTextarea, UiText } from "@shopexperts/shared";
import { useCreateExpertPayoutRequest } from "@/app/composables/wallet";
import { payoutRequestErrorMessage } from "@/app/composables/wallet/payoutRequestError";
import { formatCurrency } from "@/v2Projects/utils/display";
import { centsToDollars, dollarsToCents } from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  actingOrgId: number | null | undefined;
  availableBalanceCents: number;
  currency: string | null;
}>();

const open = defineModel<boolean>({ default: false });

const { mutateAsync: createPayoutRequest, isPending } = useCreateExpertPayoutRequest();

const amountDollars = ref<number>(0);
const notes = ref("");
const errorMessage = ref<string | null>(null);

watch(open, (isOpen) => {
  if (isOpen) {
    amountDollars.value = centsToDollars(props.availableBalanceCents);
    notes.value = "";
    errorMessage.value = null;
  }
}, { immediate: true });

async function submit() {
  errorMessage.value = null;
  if (props.actingOrgId == null) {
    errorMessage.value = "Your wallet isn't ready yet — please try again in a moment.";
    return;
  }

  const amountCents = dollarsToCents(amountDollars.value);
  if (!Number.isFinite(amountCents) || amountCents < 1) {
    errorMessage.value = "Enter an amount greater than zero.";
    return;
  }
  if (amountCents > props.availableBalanceCents) {
    errorMessage.value = `Amount can't exceed your available balance (${formatCurrency(props.availableBalanceCents, props.currency)}).`;
    return;
  }

  try {
    await createPayoutRequest({
      actingOrgId: props.actingOrgId,
      amountCents,
      notes: notes.value.trim() || null,
    });
    open.value = false;
  } catch (error) {
    errorMessage.value = payoutRequestErrorMessage(error);
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Request payout"
    :text="`You can request up to ${formatCurrency(availableBalanceCents, currency)} from your available balance. Our team will review your request.`"
    primary-action-label="Submit request"
    secondary-action-label="Cancel"
    :loading="isPending"
    @primary-action="submit"
    @secondary-action="open = false"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Amount" />
        <UiCurrencyInput v-model="amountDollars" :min="0" :step="1" :disabled="isPending" />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Note (optional)" />
        <UiTextarea
          v-model="notes"
          :rows="2"
          placeholder="Anything our team should know about this payout..."
          :disabled="isPending"
        />
      </div>

      <UiText v-if="errorMessage" as="p" variant="p-small" :text="errorMessage" class="text-red-600" />
    </div>
  </UiDialog>
</template>
